import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { InternDashboard, scoreColor, downloadPdf } from './InternDashboard';
import { SkillMasteryModal } from './SkillMasteryModal';
import {
  Users, AlertTriangle, Building2, Briefcase, GraduationCap, Archive,
  CalendarCheck, TrendingUp, ChevronRight, Check, Pencil, Download, Grid3x3,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROTATIONS = [1, 2, 3];

const LEVEL_TEXT = ['#b02a2a', '#b5571c', '#9a6a12', '#7d6c0e', '#5d6b10', '#4a7a1f', '#3d7a2a'];
const scoreTextColor = (v) => (v == null ? '#5C5C5C' : LEVEL_TEXT[Math.max(0, Math.min(6, Math.round(v)))]);

const ScoreChip = ({ value, label }) => (
  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
    style={{ backgroundColor: `${scoreColor(value)}26`, color: scoreTextColor(value) }}>
    {label}: {value != null ? value : '—'}
  </span>
);

const TotalCard = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-xl border border-[#E5E0D8] bg-white p-4 flex items-center gap-3" data-testid={`dash-total-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1A` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </span>
    <div>
      <div className="text-2xl font-bold font-['Manrope'] text-[#1A1A1A] leading-none">{value}</div>
      <div className="text-xs text-[#5C5C5C] mt-1">{label}</div>
    </div>
  </div>
);

const ProgramCard = ({ p, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [year, setYear] = useState(p.current_school_year || '');
  const [rotation, setRotation] = useState(String(p.current_rotation || 1));
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setYear(p.current_school_year || '');
    setRotation(String(p.current_rotation || 1));
  }, [p.current_school_year, p.current_rotation]);

  const openEdit = () => {
    setYear(p.current_school_year || '');
    setRotation(String(p.current_rotation || 1));
    setEditing(true);
  };
  const cancelEdit = () => {
    setYear(p.current_school_year || '');
    setRotation(String(p.current_rotation || 1));
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/programs/${p.id}`,
        { current_school_year: year, current_rotation: Number(rotation) },
        { params: { user_role: user?.role } });
      toast.success('Current term updated');
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#E5E0D8] bg-white p-4" data-testid={`dash-program-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-[#1A1A1A] font-['Manrope']">{p.name}</div>
          <div className="text-xs text-[#5C5C5C]">{[p.city, p.state].filter(Boolean).join(', ')}</div>
        </div>
        {p.needs_attention_count > 0 && (
          <Badge className="bg-[#FDECEA] text-[#d64545] hover:bg-[#FDECEA] border-0 text-xs">
            <AlertTriangle className="w-3 h-3 mr-1" />{p.needs_attention_count}
          </Badge>
        )}
      </div>

      {!editing ? (
        <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-[#5C5C5C]">
          <Badge variant="outline" className="text-xs">{p.current_school_year || 'No year set'}</Badge>
          <Badge variant="outline" className="text-xs">Rotation {p.current_rotation}</Badge>
          {canEdit && (
            <button onClick={openEdit} className="text-[#34657f] hover:underline flex items-center gap-0.5" data-testid={`dash-edit-term-${p.id}`}>
              <Pencil className="w-3 h-3" /> Set term
            </button>
          )}
        </div>
      ) : (
        <div className="mt-3 flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[11px] text-[#8A8A8A]">School year</label>
            <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2025-2026" className="h-8 w-28 text-sm" data-testid={`dash-term-year-${p.id}`} />
          </div>
          <div>
            <label className="text-[11px] text-[#8A8A8A]">Rotation</label>
            <Select value={rotation} onValueChange={setRotation}>
              <SelectTrigger className="h-8 w-20 text-sm" data-testid={`dash-term-rotation-${p.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{ROTATIONS.map((r) => <SelectItem key={r} value={String(r)}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button size="sm" className="h-8 bg-[#34657f]" onClick={save} disabled={saving} data-testid={`dash-term-save-${p.id}`}>
            <Check className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={cancelEdit}>Cancel</Button>
        </div>
      )}

      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
        <div><div className="text-lg font-bold text-[#1A1A1A]">{p.intern_count}</div><div className="text-[10px] text-[#8A8A8A] uppercase">Interns</div></div>
        <div><div className="text-lg font-bold text-[#1A1A1A]">{p.job_site_count}</div><div className="text-[10px] text-[#8A8A8A] uppercase">Sites</div></div>
        <div><div className="text-lg font-bold" style={{ color: scoreColor(p.avg_score) }}>{p.avg_score != null ? p.avg_score : '—'}</div><div className="text-[10px] text-[#8A8A8A] uppercase">Avg</div></div>
        <div><div className="text-lg font-bold text-[#34657f]">{p.attendance_rate != null ? `${p.attendance_rate}%` : '—'}</div><div className="text-[10px] text-[#8A8A8A] uppercase">Attend</div></div>
      </div>
    </div>
  );
};

const InternRow = ({ s, onOpen }) => (
  <button onClick={() => onOpen(s.id)} data-testid={`dash-intern-${s.id}`}
    className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#FAF8F4] transition-colors border border-transparent hover:border-[#E5E0D8]">
    {s.photo_url
      ? <img src={s.photo_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
      : <span className="w-9 h-9 rounded-full bg-[#34657f] text-white flex items-center justify-center text-sm font-bold shrink-0">{s.name.charAt(0)}</span>}
    <div className="min-w-0 flex-1">
      <div className="font-medium text-[#1A1A1A] truncate flex items-center gap-2">
        {s.name}
        {s.needs_attention?.length > 0 && <AlertTriangle className="w-3.5 h-3.5 text-[#d64545] shrink-0" />}
      </div>
      <div className="text-xs text-[#5C5C5C] truncate flex items-center gap-1">
        <Briefcase className="w-3 h-3" />{s.active_job_site || 'No job site'}
        {s.last_report_date && <span className="text-[#b0b0b0]">· last {s.last_report_date}</span>}
      </div>
    </div>
    <div className="hidden sm:flex items-center gap-2 shrink-0">
      <ScoreChip value={s.recent_avg} label="Score" />
      <span className="text-xs text-[#34657f] font-semibold">{s.attendance_rate != null ? `${s.attendance_rate}%` : '—'}</span>
    </div>
    <ChevronRight className="w-4 h-4 text-[#b0b0b0] shrink-0" />
  </button>
);

export const RoleDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openInternId, setOpenInternId] = useState(null);
  const [skillOpen, setSkillOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const isAdmin = user?.role === 'system_admin' || user?.role === 'team_admin';

  const exportCaseload = async () => {
    setExporting(true);
    try {
      await downloadPdf(`${API}/dashboard/caseload/pdf?archived=${archived}`,
        `${archived ? 'archived-' : ''}caseload.pdf`);
    } catch { toast.error('Could not generate the PDF. Please try again.'); } finally { setExporting(false); }
  };

  const [exportingDistrict, setExportingDistrict] = useState(false);
  const exportDistrict = async () => {
    setExportingDistrict(true);
    try {
      await downloadPdf(`${API}/dashboard/district-rollup/pdf?archived=${archived}`,
        `${archived ? 'archived-' : ''}district-rollup.pdf`);
    } catch { toast.error('Could not generate the district PDF. Please try again.'); } finally { setExportingDistrict(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/dashboard/overview`, { params: { archived } })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [archived]);

  useEffect(() => { load(); }, [load]);

  if (user?.role === 'demo') return null;

  const t = data?.totals || {};

  return (
    <Card className="dashboard-card rounded-xl" data-testid="role-dashboard">
      <CardContent className="p-5 sm:p-6 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold font-['Manrope'] text-[#1A1A1A] flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#34657f]" />
              {isAdmin ? 'Program Snapshot' : 'My Caseload'}
            </h2>
            <p className="text-sm text-[#5C5C5C]">
              {archived ? 'Showing archived (past-year) interns' : 'Showing interns in the present school year'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button variant="outline" size="sm" onClick={() => setSkillOpen(true)} data-testid="dash-skill-mastery-btn">
              <Grid3x3 className="w-4 h-4 mr-1.5" /> Skill Mastery
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={exportDistrict} disabled={exportingDistrict} data-testid="dash-district-pdf-btn">
                <Building2 className="w-4 h-4 mr-1.5" />{exportingDistrict ? 'Preparing…' : 'District PDF'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={exportCaseload} disabled={exporting} data-testid="dash-export-pdf-btn">
              <Download className="w-4 h-4 mr-1.5" />{exporting ? 'Preparing…' : (isAdmin ? 'Interns PDF' : 'Export PDF')}
            </Button>
            <Button variant={archived ? 'default' : 'outline'} size="sm"
              className={archived ? 'bg-[#34657f]' : ''}
              onClick={() => setArchived((a) => !a)} data-testid="dash-archive-toggle">
              <Archive className="w-4 h-4 mr-1.5" />
              {archived ? 'Show present year' : 'Archive'}
            </Button>
          </div>
        </div>

        {loading && <p className="text-[#5C5C5C] py-8 text-center">Loading…</p>}

        {!loading && data && isAdmin && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <TotalCard icon={Building2} label="Programs" value={t.programs || 0} color="#34657f" />
              <TotalCard icon={Users} label="Interns" value={t.interns || 0} color="#3d7a22" />
              <TotalCard icon={GraduationCap} label="Instructors" value={t.instructors || 0} color="#b34e00" />
              <TotalCard icon={Users} label="Trainers" value={t.trainers || 0} color="#1f7fa6" />
              <TotalCard icon={Briefcase} label="Job Sites" value={t.job_sites || 0} color="#7a5cc0" />
              <TotalCard icon={AlertTriangle} label="Need Attention" value={t.needs_attention || 0} color="#d64545" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.programs?.map((p) => (
                <ProgramCard key={p.id} p={p} canEdit={isAdmin} onSaved={load} />
              ))}
              {data.programs?.length === 0 && <p className="text-sm text-[#8A8A8A] col-span-2 py-4 text-center">No programs in this view.</p>}
            </div>

            {data.needs_attention?.length > 0 && (
              <div className="rounded-xl border border-[#f3c7c0] bg-[#FDECEA]/50 p-4">
                <div className="text-sm font-semibold text-[#8a2f26] mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Interns needing attention
                </div>
                <div className="space-y-1">
                  {data.needs_attention.map((s) => (
                    <InternRow key={s.id} s={s} onOpen={setOpenInternId} />
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-[#E5E0D8] bg-white">
              <div className="text-sm font-semibold text-[#1A1A1A] px-3 pt-3 pb-1 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#34657f]" />
                {archived ? 'Archived interns (past years)' : 'Interns — present school year'}
                <span className="text-xs font-normal text-[#8A8A8A]">({data.interns?.length || 0})</span>
              </div>
              <div className="divide-y divide-[#F0EDE7]">
                {data.interns?.length > 0 ? data.interns.map((s) => (
                  <InternRow key={s.id} s={s} onOpen={setOpenInternId} />
                )) : (
                  <p className="text-sm text-[#8A8A8A] py-6 text-center">{archived ? 'No archived interns.' : 'No interns in the present school year.'}</p>
                )}
              </div>
            </div>
          </>
        )}

        {!loading && data && !isAdmin && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <TotalCard icon={Users} label="My Interns" value={t.interns || 0} color="#3d7a22" />
              <TotalCard icon={AlertTriangle} label="Need Attention" value={t.needs_attention || 0} color="#d64545" />
              <TotalCard icon={TrendingUp} label="Avg Score" value={t.avg_score != null ? t.avg_score : '—'} color="#34657f" />
              <TotalCard icon={CalendarCheck} label="Attendance" value={t.attendance_rate != null ? `${t.attendance_rate}%` : '—'} color="#1f7fa6" />
            </div>
            <div className="rounded-xl border border-[#E5E0D8] bg-white divide-y divide-[#F0EDE7]">
              {data.caseload?.length > 0 ? data.caseload.map((s) => (
                <InternRow key={s.id} s={s} onOpen={setOpenInternId} />
              )) : (
                <p className="text-sm text-[#8A8A8A] py-8 text-center">
                  {archived ? 'No archived interns.' : 'No interns assigned to you yet. An administrator assigns interns to your caseload in the Program Directory.'}
                </p>
              )}
            </div>
          </>
        )}

        <InternDashboard internId={openInternId} open={!!openInternId} onOpenChange={(o) => !o && setOpenInternId(null)} />
        <SkillMasteryModal open={skillOpen} onOpenChange={setSkillOpen} archived={archived} />
      </CardContent>
    </Card>
  );
};

export default RoleDashboard;
